import { z } from 'zod';
import { IsoTimestamp, MediaRef, QuestionId, QuizId, SchoolId, UserId, } from './common.js';
import { GRADE_LEVEL_NAME_MAX } from './grade-level.js';
import { SUBJECT_NAME_MAX } from './subject.js';
import { CAMERA_CAPTURE_CADENCE_FLOOR_SEC } from './camera-capture.js';
// ---- Subject / Grade Level references ---------------------------------------
/**
 * Separator joining the Grade Level multi-select into the quiz's `gradeLevel`
 * label. A Grade Level *name* may itself contain a comma, so this is only a
 * display encoding: `gradeLevelNames` carries the same selection as a list, and
 * anything that needs the individual selections reads that instead of splitting
 * the label.
 */
export const GRADE_LEVEL_LABEL_SEPARATOR = ', ';
/** How many Grade Levels one quiz may target (mirrors the `gradeLevelIds` cap). */
export const QUIZ_GRADE_LEVEL_MAX_SELECTIONS = 32;
/**
 * Cap on observer/proctor id lists (per-window observers and the quiz-level
 * proctor list). Shared by request schemas, the stored Quiz doc, the session
 * snapshot, and the web editor — raise it in one place.
 */
export const QUIZ_OBSERVER_MAX = 250;
/**
 * Longest `gradeLevel` label the multi-select can produce: every selected name
 * at its master-data maximum, plus the separators between them. Sizing this
 * from the master domain is what keeps a valid selection savable — four
 * ordinary names ("Grade 1, Grade 2, Grade 3, Grade 4") already exceed the
 * 32-character guess this field used to carry.
 */
export const QUIZ_GRADE_LEVEL_LABEL_MAX = QUIZ_GRADE_LEVEL_MAX_SELECTIONS * GRADE_LEVEL_NAME_MAX
    + (QUIZ_GRADE_LEVEL_MAX_SELECTIONS - 1) * GRADE_LEVEL_LABEL_SEPARATOR.length;
// ---- Question ---------------------------------------------------------------
export const QuestionOption = z.object({
    id: z.string().min(1).max(8),
    text: z.string().min(1).max(2000),
    mediaRefs: z.array(MediaRef).max(4).default([]),
});
const QuestionContent = z.object({
    id: QuestionId,
    order: z.number().int().nonnegative(),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).default([]),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    /**
     * Optional per-question countdown in seconds. Absent means the question has no
     * per-question limit and the quiz-level timer is the only bound.
     *
     * It lives on the content shape (rather than on QuestionBase) so it also
     * reaches the student bundle, which is derived from QuestionContent. The
     * non-answerable LabelContentQuestion is a standalone object and therefore
     * never carries it.
     */
    durationSec: z.number().int().positive().optional(),
});
const QuestionBase = QuestionContent.extend({
    questionBankRef: z.string().max(128).optional(),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
});
/**
 * Ceiling applied to the total quiz score when a quiz turns on the optional
 * limit. The limit is off by default, so quizzes that never opt in keep the
 * unlimited behavior they have always had.
 */
export const QUESTION_MAX_SCORE_LIMIT = 100;
/**
 * The message shown when the total quiz score exceeds the limit. Shared so the
 * API refusal and the editor's inline error cannot drift apart.
 */
export function totalScoreLimitMessage() {
    return `The total score of all questions cannot exceed ${QUESTION_MAX_SCORE_LIMIT}.`;
}
/**
 * Reject when the sum of all question scores exceeds the limit. Returns null
 * when the limit is off or the total fits.
 *
 * Shared by the API routes (so a crafted request cannot bypass the editor) and
 * the quiz editor (so the author sees the problem before saving). Scores are
 * coerced rather than trusted: the PATCH guard also reads raw stored documents,
 * which no schema has parsed, so a legacy numeric string must still be caught.
 */
export function questionScoreLimitError(limitEnabled, questions) {
    if (!limitEnabled)
        return null;
    const total = questions.reduce((sum, question) => {
        const score = Number(question.maxScore);
        return sum + (Number.isFinite(score) ? score : 0);
    }, 0);
    return total > QUESTION_MAX_SCORE_LIMIT ? totalScoreLimitMessage() : null;
}
export const McqQuestion = QuestionBase.extend({
    type: z.literal('mcq'),
    options: z.array(QuestionOption).min(2).max(8),
    correctOptionId: z.string().min(1).max(8), // server-only; stripped before student bundle
});
export const MultipleAnswerScoringPolicy = z.enum(['allOrNothing', 'partialCredit']);
export const MultipleAnswerQuestion = QuestionBase.extend({
    type: z.literal('multipleAnswer'),
    options: z.array(QuestionOption).min(2).max(8),
    correctOptionIds: z.array(z.string().min(1).max(8)).min(1).max(8),
    scoringPolicy: MultipleAnswerScoringPolicy.default('allOrNothing'),
});
export const EssayQuestion = QuestionBase.extend({
    type: z.literal('essay'),
    options: z.undefined().optional(),
    correctOptionId: z.undefined().optional(),
    correctOptionIds: z.undefined().optional(),
    scoringPolicy: z.undefined().optional(),
});
export const TrueFalseQuestion = QuestionBase.extend({
    type: z.literal('trueFalse'),
    options: z.undefined().optional(),
    correctOptionId: z.undefined().optional(),
    correctOptionIds: z.undefined().optional(),
    scoringPolicy: z.undefined().optional(),
    correctValue: z.boolean(),
});
export const SHORT_ANSWER_MAX_ACCEPTED_ANSWERS = 20;
/**
 * Parse the newline-separated accepted-answers textarea used by the interactive
 * editors. The Excel workbook exchange format uses '|' instead, so an answer may
 * legitimately contain '|' here. No cap — validation reports overflow instead of
 * silently truncating.
 */
export function parseAcceptedAnswers(raw) {
    return raw
        .split('\n')
        .map((answer) => answer.trim())
        .filter((answer) => answer.length > 0);
}
export const ShortAnswerQuestion = QuestionBase.extend({
    type: z.literal('shortAnswer'),
    options: z.undefined().optional(),
    correctOptionId: z.undefined().optional(),
    correctOptionIds: z.undefined().optional(),
    scoringPolicy: z.undefined().optional(),
    // never included in the student bundle; StudentShortAnswerQuestion omits it.
    // '|' is reserved: the Excel workbook exchange format uses it as the answer
    // separator, so an answer containing it would corrupt an export/import round-trip.
    acceptedAnswers: z.array(z.string().trim().min(1).max(500).regex(/^[^|]*$/, "accepted answers must not contain '|'")).min(1).max(SHORT_ANSWER_MAX_ACCEPTED_ANSWERS),
    caseSensitive: z.boolean().default(false),
});
export const NUMERIC_ANSWER_MAX_ABS = 1e15;
/**
 * Numeric (isian angka): the student types a number and scores full marks when
 * it lands within `tolerance` of `correctAnswer`. Tolerance is absolute — 0
 * means an exact match. Both fields are server-only; StudentNumericQuestion
 * omits them.
 */
export const NumericQuestion = QuestionBase.extend({
    type: z.literal('numeric'),
    options: z.undefined().optional(),
    correctOptionId: z.undefined().optional(),
    correctOptionIds: z.undefined().optional(),
    scoringPolicy: z.undefined().optional(),
    correctAnswer: z.number().finite().min(-NUMERIC_ANSWER_MAX_ABS).max(NUMERIC_ANSWER_MAX_ABS),
    tolerance: z.number().finite().nonnegative().max(NUMERIC_ANSWER_MAX_ABS).default(0),
});
const MatchingPair = z.object({
    leftId: z.string().min(1).max(32),
    leftText: z.string().min(1).max(2000),
    rightId: z.string().min(1).max(32),
    rightText: z.string().min(1).max(2000),
});
const CorrectMatchingPair = z.object({
    leftId: z.string().min(1).max(32),
    rightId: z.string().min(1).max(32),
});
export const MatchingQuestion = QuestionBase.extend({
    type: z.literal('matching'),
    options: z.undefined().optional(),
    correctOptionId: z.undefined().optional(),
    correctOptionIds: z.undefined().optional(),
    scoringPolicy: MultipleAnswerScoringPolicy.default('allOrNothing'),
    pairs: z.array(MatchingPair).min(2).max(50),
    correctPairs: z.array(CorrectMatchingPair).min(1).max(50),
});
const OrderingItem = z.object({
    id: z.string().trim().min(1).max(32),
    text: z.string().trim().min(1).max(2000),
});
export const OrderingQuestion = QuestionBase.extend({
    type: z.literal('ordering'),
    items: z.array(OrderingItem).min(2).max(20).refine((items) => new Set(items.map((item) => item.id)).size === items.length, 'ordering item IDs must be unique'),
    scoringPolicy: MultipleAnswerScoringPolicy.default('allOrNothing'),
});
export const LabelContentQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    type: z.literal('labelContent'),
    id: QuestionId,
    order: z.number().int().nonnegative(),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).default([]),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    maxScore: z.literal(0),
});
export const Question = z.discriminatedUnion('type', [
    McqQuestion,
    MultipleAnswerQuestion,
    EssayQuestion,
    TrueFalseQuestion,
    ShortAnswerQuestion,
    NumericQuestion,
    MatchingQuestion,
    OrderingQuestion,
    LabelContentQuestion,
]);
/**
 * StudentQuestion: the variant returned in the bundle from
 * POST /v1/sessions/start. NEVER contains correctOptionId.
 * Re-derived in api/ to ensure server-side stripping.
 */
export const StudentMcqQuestion = QuestionContent.extend({
    type: z.literal('mcq'),
    options: z.array(QuestionOption).min(2).max(8),
});
export const StudentMultipleAnswerQuestion = QuestionContent.extend({
    type: z.literal('multipleAnswer'),
    options: z.array(QuestionOption).min(2).max(8),
});
export const StudentEssayQuestion = QuestionContent.extend({
    type: z.literal('essay'),
});
export const StudentTrueFalseQuestion = QuestionContent.extend({
    type: z.literal('trueFalse'),
});
export const StudentShortAnswerQuestion = QuestionContent.extend({
    type: z.literal('shortAnswer'),
});
export const StudentNumericQuestion = QuestionContent.extend({
    type: z.literal('numeric'),
});
export const StudentMatchingQuestion = QuestionContent.extend({
    type: z.literal('matching'),
    /**
     * The pairs stay in AUTHORED order: the left column is the question's
     * structure, not a choice list, so it never shuffles. `rightOrder` carries
     * the per-student display order of the right column instead — the same right
     * ids, permuted. Absent means "no shuffle" (a snapshot taken before matching
     * shuffling shipped, or a question the server did not order), and the client
     * then lists `pairs`.
     */
    pairs: z.array(MatchingPair).min(2).max(50),
    rightOrder: z.array(z.string().min(1).max(32)).min(2).max(50).optional(),
});
export const StudentOrderingQuestion = QuestionContent.extend({
    type: z.literal('ordering'),
    items: OrderingQuestion.shape.items,
});
export const StudentLabelContentQuestion = QuestionContent.extend({
    type: z.literal('labelContent'),
    maxScore: z.literal(0),
});
export const StudentQuestion = z.discriminatedUnion('type', [
    StudentMcqQuestion,
    StudentMultipleAnswerQuestion,
    StudentEssayQuestion,
    StudentTrueFalseQuestion,
    StudentShortAnswerQuestion,
    StudentNumericQuestion,
    StudentMatchingQuestion,
    StudentOrderingQuestion,
    StudentLabelContentQuestion,
]);
// ---- Quiz -------------------------------------------------------------------
export const QuizKind = z.enum(['exam', 'quiz']); // high-stakes vs low-stakes
export const QuizStatus = z.enum(['draft', 'scheduled', 'live', 'closed']);
export const QuizAuditFieldChange = z.object({
    field: z.string(),
    oldValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
});
/**
 * Which timer change an author made to a reused question inside one quiz.
 *
 * `changes` cannot express this: a per-question timer lives in the questions
 * subcollection, so there is no quiz-level field to point at, and "turned the
 * limit on" is not a value transition a reader can infer from two numbers.
 */
export const QuizTimerOverrideAction = z.enum(['enabled', 'disabled', 'updated']);
/**
 * One per-question timer override, recorded on the audit entry of the save that
 * made it. Carries everything the audit requirement names — the affected
 * question, both timer values, the action, and the quiz status at the time — so
 * a reader does not have to correlate the entry with the question documents as
 * they stood before and after.
 *
 * A null `previousDurationSec` means the question had no limit; a null
 * `newDurationSec` means it has none now.
 */
export const QuizTimerOverride = z.object({
    questionId: z.string(),
    /** Position of the question in the quiz at save time, for a human-readable reference. */
    questionOrder: z.number().int().nonnegative().nullable(),
    action: QuizTimerOverrideAction,
    previousDurationSec: z.number().int().positive().nullable(),
    newDurationSec: z.number().int().positive().nullable(),
    quizStatus: QuizStatus,
});
export const QuizAuditLogEntry = z.object({
    id: z.string(),
    schoolId: SchoolId,
    quizId: QuizId,
    actorId: z.string(),
    action: z.enum(['create', 'update', 'publish', 'archive', 'fork']),
    beforeVersion: z.number().nullable(),
    afterVersion: z.number(),
    beforeVersionId: z.string().nullable(),
    afterVersionId: z.string().nullable(),
    sourceQuizId: z.string().optional(),
    createdAt: IsoTimestamp,
    changes: z.array(QuizAuditFieldChange).optional(),
    timerOverrides: z.array(QuizTimerOverride).optional(),
    reason: z.string().max(500).nullable().optional(),
});
/** A positive whole number of seconds, or null when the question is untimed. */
export function normalizeQuestionDurationSec(value) {
    const seconds = Number(value);
    return Number.isInteger(seconds) && seconds > 0 ? seconds : null;
}
/**
 * The timer overrides a save is about to apply, computed by comparing the stored
 * questions with the ones the same request will write.
 *
 * Only questions present in BOTH sets are considered: a question that arrives
 * with the save is being added, not overridden, and one that leaves is being
 * removed — neither is a change to an existing question's limit. A question
 * whose normalized limit is unchanged produces no entry, so an unrelated edit
 * (a prompt fix) does not fill the log with no-op timer records.
 *
 * `quizStatus` is the status the quiz had when the change was made, captured
 * here because the audit requirement asks for it and the document's own status
 * field is overwritten by the same save when the request also changes status.
 */
export function quizTimerOverrides(before, after, quizStatus) {
    const previous = new Map(before.map((question) => [question.id, question]));
    const overrides = [];
    for (const question of after) {
        const stored = previous.get(question.id);
        if (!stored)
            continue;
        const previousDurationSec = normalizeQuestionDurationSec(stored.durationSec);
        const newDurationSec = normalizeQuestionDurationSec(question.durationSec);
        if (previousDurationSec === newDurationSec)
            continue;
        overrides.push({
            questionId: question.id,
            questionOrder: Number.isInteger(question.order) ? question.order : null,
            action: previousDurationSec === null ? 'enabled' : newDurationSec === null ? 'disabled' : 'updated',
            previousDurationSec,
            newDurationSec,
            quizStatus,
        });
    }
    return overrides;
}
export const QuizScheduleMode = z.enum(['global', 'class']);
export const TabSwitchPolicy = z.enum(['warn', 'pause', 'terminate']);
export const RemoteUnlockPolicy = z.object({
    enabled: z.boolean().default(false),
    requireWebcamEvidence: z.boolean().default(true),
    requestCooldownSec: z.number().int().min(30).max(900).default(60),
    requestExpiresSec: z.number().int().min(300).max(3600).default(900),
    evidenceRetentionHours: z.number().int().min(1).max(168).default(24),
});
export const IntegrityPact = z.object({
    required: z.boolean().default(false),
    version: z.string().trim().min(1).max(32).default('1'),
    title: z.string().trim().min(1).max(200).default('Academic integrity agreement'),
    body: z.string().trim().min(1).max(5000).default('I will complete this assessment independently and follow my school rules.'),
    acknowledgementText: z.string().trim().min(1).max(300).default('I understand and agree to this academic integrity agreement.'),
    updatedAt: IsoTimestamp.optional(),
    updatedBy: UserId.optional(),
});
export const IntegrityPactActorRole = z.enum(['student', 'proctor']);
export const IntegrityPactSnapshot = z.object({
    required: z.boolean(),
    version: z.string().trim().min(1).max(32),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(5000),
    acknowledgementText: z.string().min(1).max(300),
    acknowledgedAt: IsoTimestamp,
    acknowledgedBy: UserId,
    acknowledgedRole: IntegrityPactActorRole,
    expiresAt: IsoTimestamp.optional(),
    updatedAt: IsoTimestamp.optional(),
});
export const CameraPolicy = z.object({
    mode: z.enum(['disabled', 'optional', 'required']).default('disabled'),
    consentVersion: z.string().trim().min(1).max(32).default('1'),
    retentionHours: z.number().int().min(1).max(168).default(24),
    /**
     * AIMSIS-20724 — quiz-level enablement for proctor stills.
     *
     * Separate from `mode` on purpose: `mode` says whether the student's camera
     * is required/allowed at all, while this says whether a proctor may take a
     * still from the held stream. Both must be on, so enabling camera consent on
     * an existing quiz does not silently enable capture.
     */
    captureEnabled: z.boolean().default(false),
    /**
     * AIMSIS-20932 — quiz-level opt-in for AUTOMATIC stills on violations.
     *
     * When this and `captureEnabled` are both on, the violation route queues a
     * still for every reported integrity event under the same consent, cadence
     * and quota gates as a proctor request — with no proctor involved. Default
     * off: a quiz that lets a proctor take stills must not silently start
     * photographing students on every tab switch. A refused or failed still is
     * logged and never changes the exam outcome.
     */
    captureOnViolation: z.boolean().default(false),
    /**
     * Minimum seconds between two capture requests for the SAME attempt.
     *
     * The floor is the policy cadence (60 s) and is enforced here as well as in
     * the API: a document written before the floor existed parses to the default,
     * and a hand-edited document below the floor is clamped rather than trusted.
     */
    cadenceSec: z.number().int().min(CAMERA_CAPTURE_CADENCE_FLOOR_SEC).max(3600).default(CAMERA_CAPTURE_CADENCE_FLOOR_SEC),
    /** Per-attempt total quota, so cadence alone cannot produce an unbounded set. */
    maxCapturesPerAttempt: z.number().int().min(1).max(200).default(20),
    allowRecording: z.boolean().default(false),
});
export const QuizSchedule = z
    .object({
    startAt: IsoTimestamp,
    endAt: IsoTimestamp,
    durationSec: z.number().int().positive(),
    perQuestionSec: z.number().int().positive().optional(),
    lateJoinGraceSec: z.number().int().nonnegative().default(300),
    displayTimeZone: z.string().min(1).max(64).optional(),
})
    .superRefine((schedule, ctx) => {
    const windowSec = (Date.parse(schedule.endAt) - Date.parse(schedule.startAt)) / 1000;
    if (windowSec <= 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endAt'],
            message: 'end time must be after start time',
        });
    }
    else if (schedule.durationSec > windowSec) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['durationSec'],
            message: 'duration must fit within the scheduled window',
        });
    }
});
/**
 * Stored form of a class exam password (AIMSIS-20924): the plaintext is never
 * persisted — `enc` is an AES-256-GCM token sealed by the API with AAD
 * `quizId:classroomId` (api/src/lib/class-exam-password.ts, same construction
 * as totp-seed.ts). `epoch` records which rotation boundary the value was
 * minted in; a later epoch re-mints on read.
 */
export const ClassExamPassword = z.object({
    version: z.literal(1).default(1),
    enc: z.string().min(1),
    epoch: z.number().int().nonnegative().default(0),
    updatedAt: IsoTimestamp.optional(),
});
export const QuizClassSchedule = z.object({
    version: z.literal(1).default(1),
    classroomId: z.string().min(1).max(64),
    sourceClassroomId: z.string().min(1).max(64).optional(),
    sourceScheduleId: z.string().min(1).max(160).optional(),
    schedule: QuizSchedule,
    observerUserIds: z.array(UserId).max(QUIZ_OBSERVER_MAX).default([]),
    /**
     * Shared per-class exam password (legacy `eo_quiz_date.password`). Sealed
     * value; present only when the class has a password configured or rotation
     * minted one. Students may enter it instead of the quiz TOTP passcode.
     */
    examPassword: ClassExamPassword.optional(),
    source: z.object({
        system: z.enum(['aimsis', 'cbt']),
        examId: z.string().min(1).max(64).optional(),
        syncedAt: IsoTimestamp.optional(),
    }).optional(),
});
/**
 * Request-side class schedule row: the sealed `examPassword` object is never
 * client-supplied. A six-digit string sets a new password, `null` clears it,
 * and omitting the key keeps the stored value (so a sync or an unrelated
 * schedule edit does not silently drop it).
 */
export const QuizClassScheduleInput = QuizClassSchedule.omit({ examPassword: true }).extend({
    examPassword: z.string().regex(/^\d{6}$/).nullable().optional(),
});
export const QuizRules = z.object({
    shuffleQuestions: z.boolean().default(true),
    shuffleOptions: z.boolean().default(true),
    allowReview: z.boolean().default(true),
    allowBacktrack: z.boolean().default(true),
    /**
     * How many questions the student runner renders per page. `1` keeps the
     * long-standing one-question-per-screen layout; larger values show a page of
     * questions and step the previous/next controls by page.
     */
    questionsPerPage: z.number().int().min(1).max(50).default(1),
    /**
     * Whether a student may change an answer once they have moved on. `false`
     * commits a page's answers the moment a write lands on a later page, so
     * composing on the visible page stays free — taps, debounced text and
     * re-picks are all revisable — while the API refuses a later write carrying
     * a different value for a committed question (409 answer-locked). Retried
     * writes of the identical value and doubtful-flag toggles are not a redo
     * and still pass.
     */
    allowRedo: z.boolean().default(true),
    /**
     * Maximum number of attempts a student may take, `null` meaning unlimited
     * (the historical behavior). Enforced where a student-initiated retake would
     * create a new attempt — the make-up window branch of session start. A
     * proctor-issued reattempt is an explicit override and is not capped.
     */
    maxAttempts: z.number().int().min(1).max(99).nullable().default(null),
    requireFullscreen: z.boolean().default(false),
    requireSEB: z.boolean().default(false),
    /**
     * Refuse this quiz to a plain browser: students may take it only from the
     * CBT Super App (the Electron wrapper, or the Android kiosk that carries the
     * same User-Agent). Enforced server-side by middleware/superapp.ts.
     *
     * Off by default so every existing quiz keeps the browser behaviour it has
     * always had — a school opts in per quiz, and the session it creates records
     * the requirement so a later quiz edit cannot retroactively lock a running
     * attempt out of its own answers.
     *
     * The UA is client-controlled, so this deters casual browser access rather
     * than providing hard security — the same caveat as the per-tenant
     * `requireSuperAppForStudents` toggle it complements.
     */
    requireLockdownClient: z.boolean().default(false),
    /**
     * Lock the attempt while the browser runs in split-screen / multi-window.
     *
     * Defaults to `true` because the ticket scopes the control to every CBT V2
     * school; a quiz opts out by setting it to `false`. Detection is a viewport
     * heuristic — see web/src/lib/split-screen.ts for why.
     */
    blockSplitScreen: z.boolean().default(true),
    tabSwitchPolicy: TabSwitchPolicy.default('warn'),
    maxTabSwitches: z.number().int().positive().default(3),
    remoteUnlock: RemoteUnlockPolicy.default({
        enabled: false,
        requireWebcamEvidence: true,
        requestCooldownSec: 60,
        requestExpiresSec: 900,
        evidenceRetentionHours: 24,
    }),
    integrityPact: IntegrityPact.default({
        required: false,
        version: '1',
        title: 'Academic integrity agreement',
        body: 'I will complete this assessment independently and follow my school rules.',
        acknowledgementText: 'I understand and agree to this academic integrity agreement.',
    }),
    cameraPolicy: CameraPolicy.default({
        mode: 'disabled',
        consentVersion: '1',
        retentionHours: 24,
        captureEnabled: false,
        captureOnViolation: false,
        cadenceSec: CAMERA_CAPTURE_CADENCE_FLOOR_SEC,
        maxCapturesPerAttempt: 20,
        allowRecording: false,
    }),
    /**
     * TOTP rotation period in seconds.
     *
     * Legacy CBT supported per-quiz `reset_password_interval`. We mirror that:
     * 60 (default) is a strict 1-min step; finals can use 120 or 300; quick
     * pop-quizzes can stay at 60. Server computes passcode := TOTP(secret,
     * floor(serverTime / step)).
     */
    passcodeRotationSec: z.number().int().min(30).max(900).default(60),
    /**
     * Require students to enter a passcode before starting this quiz or exam.
     * Defaults on so existing quizzes retain the current passcode gate.
     */
    passcodeEnabled: z.boolean().default(true),
    /**
     * Per-class exam password rotation interval in minutes (AIMSIS-20924) —
     * parity with legacy `eo_quiz_detail.reset_password_interval`, which is in
     * minutes. Absent = never rotate (legacy null/0). When set, the password on
     * each in-window class schedule re-mints at every boundary: elapsed whole
     * minutes since the earliest class `startAt` that are a positive multiple of
     * this interval. Rotation is lazy — it runs on the first read (proctor
     * passcode poll, student `/start`, print pack) after a boundary passes, never
     * on a scheduler.
     */
    examPasswordRotationMin: z.number().int().min(1).max(1440).nullable().optional(),
    lateRequestEnabled: z.boolean().default(false),
    lateRequestShowAfterMin: z.number().int().min(0).max(1440).default(0),
    /**
     * When on, a student whose late admission request was denied may submit a
     * new request (the same document returns to `pending`) while the late-join
     * window is still open. Off by default so a denial stays terminal unless a
     * quiz opts in.
     */
    lateRequestResubmitEnabled: z.boolean().default(false),
    /**
     * Optional authoring guard: when on, the total score of all questions may not
     * exceed QUESTION_MAX_SCORE_LIMIT. Off by default so every existing quiz keeps
     * the unlimited behavior; the editor exposes it as the "Maximum score 100"
     * checkbox in the Questions section.
     *
     * The rule is enforced on writes that change a score or turn the setting on —
     * not on every update — so a quiz authored under an earlier reading of the
     * setting stays closable and correctable. See `quizScoreLimitError` in the API.
     */
    limitQuestionScoreTo100: z.boolean().default(false),
    /**
     * Practice / formative quiz: students get per-question feedback, may retake
     * without limit, no proctoring applies, and results are never delivered to
     * the AIMSIS gradebook. The API strips proctoring settings on write.
     */
    practiceMode: z.boolean().default(false),
}).superRefine((rules, ctx) => {
    if (rules.requireSEB && !rules.requireFullscreen) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['requireFullscreen'],
            message: 'fullscreen is required when Safe Exam Browser is required',
        });
    }
});
export const QuizAccess = z.object({
    passcodeSeedRef: z.string().min(1), // Secret Manager resource name or inline:v1
    passcodeSeedEnc: z.string().optional(),
    /** Static code permitted only by the API's hard-coded demo-project guard. */
    demoPasscode: z.literal('000000').optional(),
    eligibleUserIds: z.array(UserId).optional(),
    eligibleClassroomIds: z.array(z.string().max(64)).optional(),
    proctorUserIds: z.array(UserId).max(QUIZ_OBSERVER_MAX).default([]),
});
export const QuizSource = z.enum(['aimsis', 'cbt']);
/**
 * Per-quiz teacher access switches on Admin-created quizzes.
 *
 * Presence of this object marks the quiz as governed by the configurable
 * permission model: a teacher then needs BOTH an active Teaching Assignment
 * matching the quiz's subject/classroom scope AND the relevant flag enabled.
 * Absent keeps the legacy model, where `sharedAuthorIds` alone grants access —
 * teacher-created quizzes keep that behavior.
 */
export const QuizTeacherPermissions = z.object({
    view: z.boolean().default(true),
    edit: z.boolean().default(true),
    grade: z.boolean().default(true),
    publish: z.boolean().default(true),
});
export const QuizOwnership = z.object({
    ownerId: UserId,
    owningTenantId: SchoolId,
    source: QuizSource,
    immutableSourceId: z.string().min(1).max(128).optional(),
    sharedAuthorIds: z.array(UserId).max(100).default([]),
    allowedSubjectIds: z.array(z.string().min(1).max(SUBJECT_NAME_MAX)).max(100).default([]),
    allowedClassroomIds: z.array(z.string().min(1).max(64)).max(100).default([]),
    teacherPermissions: QuizTeacherPermissions.optional(),
    forkedFrom: z.object({
        quizId: QuizId,
        source: QuizSource,
        sourceVersion: z.number().int().positive().default(1),
    }).optional(),
});
export const Quiz = z.object({
    id: QuizId,
    schoolId: SchoolId,
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    subject: z.string().max(SUBJECT_NAME_MAX).optional(),
    gradeLevel: z.string().max(QUIZ_GRADE_LEVEL_LABEL_MAX).optional(),
    // Master-data references behind the labels above. `subject` / `gradeLevel`
    // stay the human-readable labels because teacher authorization, reports, and
    // exports match on them; these ids record which
    // `schools/{schoolId}/subjects` and `.../gradeLevels` record was chosen, so a
    // rename keeps the selection resolvable and the API can validate it.
    subjectId: z.string().min(1).max(64).nullable().optional(),
    gradeLevelIds: z.array(z.string().min(1).max(64)).max(QUIZ_GRADE_LEVEL_MAX_SELECTIONS).optional(),
    // The selected Grade Level names as a list. `gradeLevel` is the same values
    // joined for display, which is lossy when a name contains the separator, so
    // validation and rehydration read this instead. Absent on documents saved
    // before it existed; readers then fall back to splitting the label.
    gradeLevelNames: z.array(z.string().min(1).max(GRADE_LEVEL_NAME_MAX)).max(QUIZ_GRADE_LEVEL_MAX_SELECTIONS).optional(),
    kind: QuizKind,
    status: QuizStatus,
    scheduleMode: QuizScheduleMode.default('global'),
    schedule: QuizSchedule,
    classSchedules: z.array(QuizClassSchedule).max(100).optional(),
    // Denormalized latest end across the base schedule and every class schedule,
    // maintained on write so /eligible can filter out fully-ended quizzes in the
    // query (a class window may end later than the base schedule.endAt).
    effectiveEndAt: IsoTimestamp.optional(),
    rules: QuizRules,
    access: QuizAccess,
    createdBy: UserId,
    ownership: QuizOwnership.optional(),
    version: z.number().int().positive().default(1),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    archivedAt: IsoTimestamp.nullable().optional(),
});
/**
 * Latest moment any cohort can still be taking this quiz: the max endAt across
 * the base schedule and all per-class schedule overrides. Stored as
 * Quiz.effectiveEndAt so a single indexed query can exclude ended quizzes.
 */
export function computeEffectiveEndAt(schedule, classSchedules) {
    let maxMs = Date.parse(schedule.endAt);
    for (const entry of classSchedules ?? []) {
        const ms = Date.parse(entry?.schedule?.endAt ?? '');
        if (Number.isFinite(ms) && ms > maxMs)
            maxMs = ms;
    }
    return Number.isFinite(maxMs) ? new Date(maxMs).toISOString() : schedule.endAt;
}
//# sourceMappingURL=quiz.js.map